<template>
    <div>
        <pc-select
            v-if="property.ValueOptions"
            v-model="selectValue"
            v-bind:data-test-role="`testRole.property.${property.DisplayName}`"
            class="mb-3"
            v-bind:disabled="disabled"
            v-bind:label="property.DisplayName"
            v-bind:show-help="showHelp"
            v-bind:options="
                property.ValueOptions.map((x) => {
                    return {
                        text: x,
                        value: x
                    };
                })
            "
            v-bind:validator="validator"
            v-bind:required="property.IsRequired"
            v-on:help-clicked="onHelpClicked($event)"
            v-on:validated="onValidated($event)" />
        <sensitive-input
            v-else-if="property.InputType == 'sensitive'"
            v-model="modelValueWrapper"
            class="mb-3"
            v-bind:can-edit="!disabled"
            v-bind:can-show="canShowSensitiveValues"
            v-bind:label="property.DisplayName"
            v-bind:show-help="showHelp"
            v-bind:validator="validator"
            v-bind:required="property.IsRequired"
            v-on:help-clicked="onHelpClicked($event)"
            v-on:validated="onValidated($event)"
            v-on:plaintext-requested="onGetPlaintextValueRequested" />
        <headers-editor
            v-else-if="property.InputType == 'headers'"
            v-model="modelValueWrapper"
            class="mb-3"
            v-bind:disabled="disabled"
            v-bind:input-property-id="property.PublicId"
            v-bind:authorization-in-use="validateReservedHeaders && authorizationHeaderInUse"
            v-bind:validate-reserved-headers="validateReservedHeaders"
            v-bind:data-test-role="`testRole.property.${property.DisplayName}`"
            v-bind:label="property.DisplayName"
            v-on:validated="onValidated($event)"
            v-on:plaintext-requested="onGetPlaintextValueRequested" />
        <pc-input
            v-else-if="property.InputType == 'password'"
            v-model="modelValueWrapper"
            v-bind:data-test-role="`testRole.property.${property.DisplayName}`"
            type="password"
            class="mb-3"
            v-bind:disabled="disabled"
            v-bind:label="property.DisplayName"
            v-bind:show-help="showHelp"
            v-bind:validator="validator"
            v-bind:required="property.IsRequired"
            v-on:validated="onValidated($event)"
            v-on:help-clicked="onHelpClicked($event)" />
        <pc-checkbox
            v-else-if="property.FieldType === booleanFieldType"
            v-model="boolValue"
            v-bind:label="property.DisplayName"
            v-bind:show-help="showHelp"
            v-bind:disabled="disabled"
            v-on:help-clicked="onHelpClicked($event)" />
        <pc-input
            v-else
            v-model="modelValueWrapper"
            v-bind:data-test-role="`testRole.property.${property.DisplayName}`"
            type="text"
            class="mb-3"
            v-bind:disabled="disabled"
            v-bind:label="property.DisplayName"
            v-bind:show-help="showHelp"
            v-bind:validator="validator"
            v-bind:required="property.IsRequired"
            v-on:validated="onValidated($event)"
            v-on:help-clicked="onHelpClicked($event)" />
    </div>
</template>

<script>
    import {
        PROPERTY_TYPES,
        CONNECTION_FIELD_TYPES,
        getValidatorTypesForFieldType,
        PROPERTY_SPECIAL_VALIDATION
    } from "Util/webAdapterUtil";
    import headersEditor from "./headersEditor.vue";
    import sensitiveInput from "./sensitiveInput.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/connectionPropertyEditor.i18n";

    export default {
        name: "ConnectionPropertyEditor",
        components: {
            headersEditor,
            sensitiveInput
        },
        inject: {
            validationHub: { default: null }
        },
        provide() {
            return {
                validationHub: this.alternateValidationHub ?? this.validationHub
            };
        },
        props: {
            authorizationHeaderInUse: { type: Boolean, required: false, default: false },
            property: { type: Object, required: true },
            disabled: { type: Boolean, required: false, default: false },
            canShowSensitiveValues: { type: Boolean, required: true },
            showHelp: { type: Boolean, required: false, default: false },
            modelValue: { type: String, required: true },
            alternateValidationHub: { type: Object, required: false, default: null }
        },
        emits: ["propertyChanged", "getPlaintextValueRequested", "validated", "help-clicked", "update:modelValue"],
        data: function () {
            return {
                nameValidator: getValidator(this.$app, VALIDATOR_TYPES.required),

                configurablePropertyType: PROPERTY_TYPES.readWrite,
                booleanFieldType: CONNECTION_FIELD_TYPES.boolean
            };
        },
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            isPerConfiguration() {
                return this.property.PropertyType === this.configurablePropertyType;
            },
            modelValueWrapper: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.onInput(value);
                    this.onPropertyChanged(this.isPerConfiguration);
                }
            },
            selectValue: {
                get() {
                    if (this.modelValue) {
                        return this.modelValue;
                    } else {
                        return "";
                    }
                },
                set(value) {
                    this.onInput(value);
                    this.onPropertyChanged(this.isPerConfiguration);
                }
            },
            boolValue: {
                get() {
                    return this.modelValue?.toLowerCase() === "true";
                },
                set(value) {
                    this.onInput(value ? "true" : "false");
                    this.onPropertyChanged(this.isPerConfiguration);
                }
            },
            validator() {
                if (this.disabled) {
                    // No validation necessary if it is disabled
                    return null;
                }
                const validators = getValidatorTypesForFieldType(this.property.FieldType);
                let specialValidator = null;
                switch (this.property.SpecialValidation) {
                    case PROPERTY_SPECIAL_VALIDATION.email:
                        specialValidator = VALIDATOR_TYPES.email;
                        break;
                    case PROPERTY_SPECIAL_VALIDATION.httpsOnly:
                        specialValidator = {
                            type: VALIDATOR_TYPES.custom,
                            validate: this._isUrlHttps,
                            errorMsg: this.httpsRequiredErrorMessage
                        };
                        break;
                }

                if (this.property.IsRequired) {
                    validators.push(VALIDATOR_TYPES.required);
                }
                if (specialValidator) {
                    validators.push(specialValidator);
                }
                return getValidator(this.$app, validators);
            },
            validateReservedHeaders() {
                return this.property.SpecialValidation === PROPERTY_SPECIAL_VALIDATION.reservedHeaders;
            }
        },
        methods: {
            onPropertyChanged(isPerConfiguration) {
                this.$emit("propertyChanged", isPerConfiguration);
            },
            onGetPlaintextValueRequested(succcessCallback, cancelCallback) {
                this.$emit("getPlaintextValueRequested", this.property, succcessCallback, cancelCallback);
            },
            onValidated(value) {
                this.$emit("validated", value);
            },
            onHelpClicked(event) {
                this.$emit("help-clicked", event);
            },
            onInput(value) {
                this.$emit("update:modelValue", value);
            },
            _isUrlHttps(url) {
                return url.startsWith("https://");
            }
        }
    };
</script>

<style lang="scss" scoped>
    .capsule-icon {
        border: 0;
        width: 1.5rem;
        height: 1.5rem;
        filter: invert(100%);
    }
</style>
